<script setup lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { NON_STREAM_STANDARD_MAX_POST_SIZE, NON_STREAM_WIDE_MAX_POST_SIZE } from '@@/bits/post_size'
import sectionSorter from '@@/bits/section_sorter'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { defineAsyncComponent, vDel } from '@@/bits/vue'
import { WallWishGroupByTypes } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Id } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostsDraggablePad from '@@/vuecomponents/SurfacePostsDraggablePad.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { useDragSortableInGroups } from '@@/vuecomposables/surface_posts_drag_sortable_in_groups'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { useSurfaceSectionsDragSortable } from '@@/vuecomposables/surface_sections_drag_sortable'
import { storeToRefs } from 'pinia'
import type Vue from 'vue'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfacePostsStore = useSurfacePostsStore()
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()
const surfaceSectionsStore = useSurfaceSectionsStore()

const { backgroundLuminance, dir, isReadOnly, isScreenshotMode, isPostSizeStandard, isPostSizeWide, isFrozen } =
  storeToRefs(useSurfaceStore())
const { isAPostDragging, groupIdUnderPostBeingDragged } = storeToRefs(surfacePostsDragAndDropStore)
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { isASectionDragging } = storeToRefs(surfaceSectionsStore)
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { sortedGroups, wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())

const { shouldDisplayPost, shouldDisplayGroup } = surfacePostsStore
const { canShowDraggingOverlayOnGroup } = surfacePostsDragAndDropStore
const { windowHeight } = storeToRefs(useWindowSizeStore())

const rootElement: Ref<HTMLDivElement | null> = ref(null)

const overlayWidth = computed(() => {
  // wudth on shelf/grid with sections is either 256 or 380
  const postElementWidth = isPostSizeStandard.value
    ? document.querySelector('.wish-v2')?.getBoundingClientRect().width ?? NON_STREAM_STANDARD_MAX_POST_SIZE
    : document.querySelector('.wish-v2')?.getBoundingClientRect().width ?? NON_STREAM_WIDE_MAX_POST_SIZE
  // add extra width to the overlay so it extends beyond posts
  return postElementWidth ? postElementWidth + 12 : 0
})

const overlayTopPosition = computed(() => {
  const defaultTopMargin = 0
  if (groupIdUnderPostBeingDragged.value === undefined) return defaultTopMargin
  const hoveredSectionHeaderHeight = postGroupHeaderHeights.value[groupIdUnderPostBeingDragged.value]
  if (!hoveredSectionHeaderHeight) return defaultTopMargin

  if (hoveredSectionHeaderHeight === maxPostGroupHeaderHeight.value) return defaultTopMargin

  return maxPostGroupHeaderHeight.value - hoveredSectionHeaderHeight
})

function assignRootElementRef(v: Vue | null): void {
  rootElement.value = v?.$el as HTMLDivElement | null
}
useSurfaceSectionsDragSortable({
  onDragStart: () => {
    isASectionDragging.value = true
  },
  onDragEnd: ({ groupId }) => {
    isASectionDragging.value = false
    if (groupId) {
      const sectionElement = document.querySelector(`#section-${groupId}`) as HTMLElement | undefined
      if (!sectionElement) return
      const sortResult = sectionSorter.getNewIndex(sectionElement)
      if (!sortResult) return
      surfaceSectionsStore.sortSection(sortResult)
    }
  },
})
useDragSortableInGroups({
  onDragStart: ({ groupId }) => {
    isAPostDragging.value = true
    groupIdUnderPostBeingDragged.value = groupId
  },
  onDragEnd: ({ postCid, postId, groupId }) => {
    isAPostDragging.value = false
    if (postCid && postId) {
      const sectionId =
        typeof groupId === 'number' && wishGroupBy.value === WallWishGroupByTypes.Section ? groupId : undefined
      const prevWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const nextWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${postId}`, {
        sectionId,
        prevId: prevWish?.id,
        nextId: nextWish?.id,
      })
      surfacePostsStore.sortPost({
        postCid,
        sortIndex: sortIndex ?? undefined,
        sectionId,
        gapSize,
      })
    }
    groupIdUnderPostBeingDragged.value = undefined
  },
  format: 'grid',
})

useScrollToNewestPost(rootElement)

const postGroupHeaderHeights = ref<Record<Group['id'], number>>({})

function updateMaxGroupHeaderHeight(groupId: Group['id'], { height }: DOMRectReadOnly): void {
  postGroupHeaderHeights.value = {
    ...postGroupHeaderHeights.value,
    [groupId]: height,
  }
}

/**
 * Make sure the group won't be scrolled horizontally on Safari
 * @param e
 */
function handleGroupScroll(e: Event) {
  if (!device.safari) return
  if (!isAPostDragging.value) return
  const target = e.target as HTMLElement
  if (target.scrollLeft > 0) {
    target.scrollLeft = 0
    e.preventDefault()
  }
}

const maxPostGroupHeaderHeight = computed(() => Math.max(...Object.values(postGroupHeaderHeights.value)))
const minHeight = computed(() => {
  // Set minHeight as 1/4 of windowHeight since we assume that each post fills 1/4 of the screen's height
  return windowHeight.value / 4
})
const minWidth = computed(() => {
  return isPostSizeStandard.value ? NON_STREAM_STANDARD_MAX_POST_SIZE : NON_STREAM_WIDE_MAX_POST_SIZE
})
</script>

<template>
  <SurfacePostsDraggablePad
    :ref="assignRootElementRef"
    :dir="dir"
    :class="[
      'flex',
      'flex-row',

      'space-s-4',

      // Removing overflow classes increase document.documentElement.scrollWidth and allows full screenshot with full_width param
      !isScreenshotMode && 'overflow-y-hidden overflow-x-auto',
      {
        'scrollbar-photo': backgroundLuminance === 'light',
        'scrollbar-photo-dark': backgroundLuminance === 'dark',
      },
      'mx-auto',
      'box-border',
      'w-full',
      'px-3 tablet-portrait:px-4',
    ]"
  >
    <section
      v-for="group in sortedGroups"
      v-show="shouldDisplayGroup(group)"
      :id="'section-' + group.id"
      :key="group.id"
      :data-id="group.id"
      :data-rank="group.sort_index"
      :class="[
        'relative',
        'flex-none',
        'flex',
        'flex-col',
        // This list of sections should be centered on the screen.
        // Normally, we can do it with `justify-center` inside a
        // flex container. But since the content can overflow
        // horizontally, we can't use it but instead need to
        // set `margin-left: auto` and `margin-right: auto` on the
        // first and last elements respectively.
        // See: https://stackoverflow.com/a/24678299/8943850
        'first:!ms-auto',
        'last:!me-auto',
        isPostSizeStandard && 'w-[272px]', // max width of post + scrollbar so 256px + 16px = 272px
        isPostSizeWide && 'w-[396px]', // max width of post + scrollbar so 380px + 16px = 396px
        {
          'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
        },
      ]"
    >
      <div
        v-if="canShowDraggingOverlayOnGroup(group.id)"
        :class="[
          'absolute -ms-2 z-underneath',
          'border-solid border-2 rounded-xl shadow-elevation-2',
          'pointer-events-none',
          {
            'border-grape-500 bg-modal-overlay-grape-alpha-20': backgroundLuminance === 'light',
            'border-canary-500 bg-modal-overlay-canary-alpha-20': backgroundLuminance === 'dark',
          },
        ]"
        :style="{ height: `calc(100% + 8px)`, width: `${overlayWidth}px`, top: `${overlayTopPosition}px` }"
      />

      <!-- GROUP HEADER -->
      <div
        v-if="wishGroupBy !== WallWishGroupByTypes.None"
        :class="[
          'flex-none flex flex-col justify-end mb-3 tablet-portrait:mb-4 mt-2',
          isPostSizeStandard && 'max-w-64',
          isPostSizeWide && 'max-w-95',
        ]"
        :style="{ height: `${maxPostGroupHeaderHeight}px` }"
      >
        <SurfacePostGroupHeaderSection
          v-if="wishGroupBy === WallWishGroupByTypes.Section"
          :section="group"
          @resize="updateMaxGroupHeaderHeight(group.id, $event)"
          @before-unmount="vDel(postGroupHeaderHeights, group.id)"
        />
      </div>

      <!-- POSTS IN GROUP -->
      <div
        :id="`group-posts-${group.id}`"
        :data-id="group.id"
        :class="[
          'grow',
          'flex',
          'flex-col',
          'space-y-2',
          'overflow-y-auto',
          'overflow-x-hidden',
          'group-posts',
          '-ms-1.5 ps-1.5',
          {
            'scrollbar-photo': backgroundLuminance === 'light',
            'scrollbar-photo-dark': backgroundLuminance === 'dark',
          },
        ]"
        @scroll="handleGroupScroll"
      >
        <template v-if="isAppUsing('lazyRenderPosts')">
          <template v-for="(post, index) in allSortedPostsByGroup[group.id]">
            <SurfaceLazy
              v-show="shouldDisplayPost(post.cid)"
              :key="post.cid"
              :data-cid="post.cid"
              :data-id="post.id"
              :min-height="minHeight"
              :min-width="minWidth"
              class="wish-wrapper h-fit last:!mb-8 tablet-portrait:last:mb-4 -ms-1.5 ps-1.5 -me-3 pe-3.5"
            >
              <SurfacePost
                :class="[
                  // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/grid.scss
                  'wish-v2',
                  isPostSizeStandard && 'max-w-64',
                  isPostSizeWide && 'max-w-95',
                ]"
                :index="index"
                :post="post"
                :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] : []"
                :post-comment-ids="post.id ? commentIdsByPostId[post.id] : []"
              />
            </SurfaceLazy>
          </template>
        </template>

        <template v-else>
          <div
            v-for="(post, index) in allSortedPostsByGroup[group.id]"
            v-show="shouldDisplayPost(post.cid)"
            :key="post.cid"
            :data-cid="post.cid"
            :data-id="post.id"
            class="wish-wrapper h-fit last:!mb-8 tablet-portrait:last:mb-4 -ms-1.5 ps-1.5 -me-3 pe-3.5"
          >
            <SurfacePost
              :class="[
                // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/grid.scss
                'wish-v2',
                isPostSizeStandard && 'max-w-64',
                isPostSizeWide && 'max-w-95',
              ]"
              :index="index"
              :post="post"
              :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] : []"
              :post-comment-ids="post.id ? commentIdsByPostId[post.id] : []"
            />
          </div>
        </template>
      </div>
    </section>

    <div
      v-if="
        wishGroupBy === WallWishGroupByTypes.Section &&
        canIModerate &&
        !isReadOnly &&
        !isFrozen &&
        !isDemoPadletPanelDesktop
      "
      key="surface-add-section-button"
      class="flex-none flex flex-col items-start justify-end me-auto w-fit mt-2"
      :style="{ height: `${maxPostGroupHeaderHeight}px`, marginInlineEnd: 'auto' }"
    >
      <SurfaceAddSectionButton class="mb-11 tablet-portrait:mb-12" />
    </div>
  </SurfacePostsDraggablePad>
</template>
